import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import Button from '../components/UI/Button';
import ResponsiveTable from '../components/ResponsiveTable';
import StatusBadge from '../components/UI/StatusBadge';
import { useTableSort } from '../components/UI/tableSort';
import { apiUrl } from '../config/api';
import KpiCard from '../components/Admin/KpiCard';
import { exportToCsv } from '../utils/adminExport';
import { AuthContext } from '../context/AuthContext';
import { auditModalView } from '../utils/auditClient';
import { toCurrency } from '../utils/adminMetrics';
import Pager from '../components/UI/Pager';

const PAGE_SIZE = 10;

const EMPTY_FORM = {
  expense_code: '',
  expense_name: '',
  calculation_method: 'percentage',
  default_rate_percent: '',
  default_fixed_amount: '',
  description: '',
  is_active: true,
};

const ManageLoanExpenseTypes = () => {
  const { user } = useContext(AuthContext);
  const canManage = Array.isArray(user?.permissions) && user.permissions.includes('ManageConfig');

  const [types, setTypes]             = useState([]);
  const [error, setError]             = useState(null);
  const [showModal, setShowModal]     = useState(false);
  const [selected, setSelected]       = useState(null);
  const [formData, setFormData]       = useState(EMPTY_FORM);
  const [formErrors, setFormErrors]   = useState({});
  const [message, setMessage]         = useState('');
  const [confirmAction, setConfirmAction] = useState(null);
  const [searchTerm, setSearchTerm]   = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => { fetchTypes(); }, []);

  const fetchTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(apiUrl('/api/loan-expense-types?all=1'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTypes(data || []);
      setError(null);
    } catch (err) {
      setError(`Error fetching expense types: ${err.response?.data?.error || err.message}`);
    }
  };

  // ── KPI metrics ────────────────────────────────────────────────────────────
  const total       = types.length;
  const active      = types.filter(t => t.is_active).length;
  const inactive    = total - active;
  const pctCount    = types.filter(t => t.calculation_method === 'percentage').length;
  const fixedCount  = types.filter(t => t.calculation_method === 'fixed').length;

  // ── Form validation ─────────────────────────────────────────────────────────
  const validate = () => {
    const errs = {};
    if (!formData.expense_code.trim()) errs.expense_code = 'Expense code is required';
    if (!formData.expense_name.trim()) errs.expense_name = 'Expense name is required';
    if (formData.calculation_method === 'percentage') {
      const v = parseFloat(formData.default_rate_percent);
      if (isNaN(v) || v < 0) errs.default_rate_percent = 'A valid rate (%) is required';
    } else {
      const v = parseFloat(formData.default_fixed_amount);
      if (isNaN(v) || v < 0) errs.default_fixed_amount = 'A valid fixed amount is required';
    }
    setFormErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ── Modal helpers ───────────────────────────────────────────────────────────
  const openModal = (type = null) => {
    setSelected(type);
    setFormData(type
      ? {
          expense_code:         type.expense_code,
          expense_name:         type.expense_name,
          calculation_method:   type.calculation_method,
          default_rate_percent: type.default_rate_percent ?? '',
          default_fixed_amount: type.default_fixed_amount ?? '',
          description:          type.description || '',
          is_active:            type.is_active,
        }
      : EMPTY_FORM
    );
    setFormErrors({});
    setMessage('');
    setShowModal(true);
    auditModalView(type ? 'Edit Loan Expense Type' : 'Add Loan Expense Type');
  };

  const closeModal = () => {
    setShowModal(false);
    setSelected(null);
    setFormData(EMPTY_FORM);
    setFormErrors({});
  };

  // ── CRUD handlers ───────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validate()) return;
    const token = localStorage.getItem('token');
    const payload = {
      expense_code:        formData.expense_code.trim().toUpperCase(),
      expense_name:        formData.expense_name.trim(),
      calculation_method:  formData.calculation_method,
      default_rate_percent:
        formData.calculation_method === 'percentage' ? parseFloat(formData.default_rate_percent) : undefined,
      default_fixed_amount:
        formData.calculation_method === 'fixed' ? parseFloat(formData.default_fixed_amount) : undefined,
      description: formData.description || null,
      is_active:   formData.is_active,
    };
    try {
      if (selected) {
        await axios.put(apiUrl(`/api/loan-expense-types/${selected.id}`), payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setMessage('Expense type updated successfully');
      } else {
        await axios.post(apiUrl('/api/loan-expense-types'), payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setMessage('Expense type added successfully');
      }
      closeModal();
      fetchTypes();
    } catch (err) {
      const msg = err.response?.data?.error || (selected ? 'Error updating expense type' : 'Error adding expense type');
      setMessage(msg);
    }
  };

  const handleToggle = async (id, currentActive, codeName) => {
    const token = localStorage.getItem('token');
    try {
      await axios.patch(apiUrl(`/api/loan-expense-types/${id}/toggle`), {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessage(`${codeName} ${currentActive ? 'disabled' : 'enabled'} successfully`);
      setConfirmAction(null);
      fetchTypes();
    } catch (err) {
      setMessage(err.response?.data?.error || 'Error toggling expense type');
    }
  };

  const requestToggle = (type) => {
    setConfirmAction({ id: type.id, is_active: type.is_active, label: type.expense_name });
  };

  const handleDelete = async (id, codeName) => {
    const token = localStorage.getItem('token');
    try {
      await axios.delete(apiUrl(`/api/loan-expense-types/${id}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setMessage(`${codeName} deleted successfully`);
      fetchTypes();
    } catch (err) {
      setMessage(err.response?.data?.error || 'Error deleting expense type');
    }
  };

  // ── Auto‑clear message ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(''), 6000);
    return () => clearTimeout(t);
  }, [message]);

  // ── Search + pagination ─────────────────────────────────────────────────────
  useEffect(() => { setCurrentPage(1); }, [searchTerm]);

  const filtered = types.filter(t =>
    t.expense_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.expense_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.calculation_method.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (t.description || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (t.is_active ? 'active' : 'inactive').includes(searchTerm.toLowerCase())
  );
  const totalPages    = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  // A click on a column sorts every type, then the page is taken.
  const typeSort      = useTableSort(filtered);
  const paginated     = typeSort.rows.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const msgClass = () => {
    if (message.toLowerCase().includes('error') || message.toLowerCase().includes('cannot')) {
      return 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300';
    }
    if (message.toLowerCase().includes('success') || message.toLowerCase().includes('deleted') || message.toLowerCase().includes('enabled') || message.toLowerCase().includes('disabled')) {
      return 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300';
    }
    return 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300';
  };

  const methodBadge = (method) =>
    method === 'percentage'
      ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
      : 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300';

  const displayValue = (type) =>
    type.calculation_method === 'percentage'
      ? `${Number(type.default_rate_percent).toFixed(4)}%`
      : toCurrency(Number(type.default_fixed_amount));

  return (
    <div className="max-w-full min-h-screen">

      {/* KPI Row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-2">
        <KpiCard label="Total Types"       value={total}      color="gray"  />
        <KpiCard label="Active"            value={active}     color="green" />
        <KpiCard label="Inactive"          value={inactive}   color={inactive > 0 ? 'amber' : 'green'} />
        <KpiCard label="% Rate / Fixed"    value={`${pctCount} / ${fixedCount}`} color="blue" />
      </div>

      {error   && <div className="p-2 rounded-lg mb-2 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300">{error}</div>}
      {message && (
        <div className={`p-2 rounded-lg mb-2 flex items-center justify-between ${msgClass()}`}>
          <span>{message}</span>
          <Button onClick={() => setMessage('')} variant="text" size="sm" className="ml-4 rounded-full" aria-label="Close">&times;</Button>
        </div>
      )}

      {/* Confirm toggle banner */}
      {confirmAction && (
        <div className="flex items-center justify-between bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 p-2 rounded mb-4 shadow border border-yellow-200 dark:border-yellow-700/50">
          <span>
            Are you sure you want to <strong>{confirmAction.is_active ? 'disable' : 'enable'}</strong>{' '}
            <strong>{confirmAction.label}</strong>?
          </span>
          <div className="flex gap-2">
            <Button
              onClick={() => handleToggle(confirmAction.id, confirmAction.is_active, confirmAction.label)}
              variant={confirmAction.is_active ? 'danger' : 'success'}
              size="sm"
              busyLabel="Working…"
            >
              Confirm
            </Button>
            <Button onClick={() => setConfirmAction(null)} variant="secondary" size="sm">Cancel</Button>
          </div>
        </div>
      )}

      {/* Toolbar */}
      <div className="mb-2 flex flex-col sm:flex-row justify-between items-center gap-2">
        <input
          type="text"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Search expense types..."
          className="p-2 w-full sm:max-w-xs"
        />
        <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
          {canManage && (
            <Button onClick={() => openModal()} variant="primary" size="md">Add Expense Type</Button>
          )}
          {canManage && (
            <Button
              onClick={() => exportToCsv('loan_expense_types.csv', types.map(t => ({
                ID:                 t.id,
                Code:               t.expense_code,
                Name:               t.expense_name,
                Method:             t.calculation_method,
                'Rate %':           t.default_rate_percent ?? '',
                'Fixed Amount':     t.default_fixed_amount ?? '',
                Description:        t.description || '',
                Active:             t.is_active ? 'Yes' : 'No',
              })))}
              variant="secondary"
              size="md"
            >
              Export CSV
            </Button>
          )}
          {/* Pagination controls */}
          <Pager page={currentPage} pages={totalPages} onPage={setCurrentPage} />
        </div>
      </div>

      <div className="bg-white dark:bg-slate-900">
        <ResponsiveTable
          data={paginated}
          sort={typeSort.sort}
          onSortChange={typeSort.onSortChange}
          columns={[
            { field: 'expense_code', header: 'Code', formatter: (value) => (
                <span className="font-mono font-semibold text-gray-800 dark:text-slate-200">{value}</span>
              ) },
            { field: 'expense_name', header: 'Name', formatter: (value) => (
                <span className="text-gray-700 dark:text-slate-200">{value}</span>
              ) },
            { field: 'calculation_method', header: 'Method', formatter: (value) => (
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold capitalize ${methodBadge(value)}`}>
                  {value}
                </span>
              ) },
            { field: 'default_rate_percent', header: 'Default Value', formatter: (_, type) => (
                <span className="font-medium text-gray-800 dark:text-slate-200">{displayValue(type)}</span>
              ) },
            { field: 'description', header: 'Description', formatter: (value) => (
                <span className="text-gray-600 dark:text-slate-300">{value || '—'}</span>
              ) },
            { field: 'is_active', header: 'Status', formatter: (value) => (
                <StatusBadge status={value ? 'Active' : 'Inactive'} />
              ) },
          ]}
          actions={canManage ? [
            {
              label: 'Edit',
              onClick: (type) => openModal(type),
              className: 'bg-blue-50 text-blue-600 hover:bg-blue-100 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50',
            },
            {
              label: 'Disable',
              onClick: (type) => requestToggle(type),
              condition: (type) => type.is_active,
              className: 'bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50',
            },
            {
              label: 'Enable',
              onClick: (type) => requestToggle(type),
              condition: (type) => !type.is_active,
              className: 'bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-900/30 dark:text-green-300 dark:hover:bg-green-900/50',
            },
            {
              label: 'Delete',
              onClick: (type) => handleDelete(type.id, type.expense_name),
              className: 'bg-red-50 text-red-700 hover:bg-red-100 dark:bg-red-900/30 dark:text-red-300 dark:hover:bg-red-900/50',
            },
          ] : []}
          emptyMessage="No results found."
        />
      </div>

      {/* Add / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div
            className="bg-white dark:bg-slate-900 rounded-xl overflow-hidden w-full max-w-md"
            style={{ boxShadow: '0 20px 40px -8px rgba(0,0,0,0.18), 0 4px 16px -4px rgba(79,70,229,0.12)' }}
          >
            {/* Gradient accent bar */}
            <div className="h-1" style={{ background: 'linear-gradient(90deg, #3b82f6, #6366f1, #8b5cf6)' }} />

            {/* Modal header */}
            <div
              className="app-modal-header px-5 py-3.5 border-b border-indigo-100/60 dark:border-slate-700 flex items-center justify-between"
            >
              <h3 className="text-sm font-semibold text-gray-800 dark:text-slate-100 flex items-center gap-2">
                <span className="w-1 h-4 rounded-full bg-gradient-to-b from-blue-500 to-indigo-500 inline-block flex-shrink-0" />
                {selected ? 'Edit Expense Type' : 'Add Expense Type'}
              </h3>
              <button
                type="button"
                onClick={closeModal}
                className="p-1.5 rounded-full text-gray-400 hover:bg-indigo-100/60 hover:text-indigo-600 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Modal body */}
            <form
              className="p-5 space-y-4"
              onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}
            >
              {/* Expense Code */}
              <div>
                <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Expense Code</label>
                <input
                  type="text"
                  value={formData.expense_code}
                  onChange={e => setFormData({ ...formData, expense_code: e.target.value })}
                  placeholder="e.g. NAMFISA_LEVY"
                  className={`w-full ${formData.expense_code.trim() ? '' : 'border-rose-300 dark:border-rose-700/60 focus:border-rose-500 focus:ring-rose-500/20'} font-mono`}
                />
                {formErrors.expense_code && <p className="text-rose-500 text-xs mt-1">{formErrors.expense_code}</p>}
              </div>

              {/* Expense Name */}
              <div>
                <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Expense Name</label>
                <input
                  type="text"
                  value={formData.expense_name}
                  onChange={e => setFormData({ ...formData, expense_name: e.target.value })}
                  placeholder="e.g. NAMFISA Levy"
                  className={`w-full ${formData.expense_name.trim() ? '' : 'border-rose-300 dark:border-rose-700/60 focus:border-rose-500 focus:ring-rose-500/20'}`}
                />
                {formErrors.expense_name && <p className="text-rose-500 text-xs mt-1">{formErrors.expense_name}</p>}
              </div>

              {/* Calculation Method */}
              <div>
                <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Calculation Method</label>
                <select
                  value={formData.calculation_method}
                  onChange={e => setFormData({ ...formData, calculation_method: e.target.value, default_rate_percent: '', default_fixed_amount: '' })}
                  className="w-full"
                >
                  <option value="percentage">Percentage (% of loan amount)</option>
                  <option value="fixed">Fixed Amount (N$)</option>
                </select>
              </div>

              {/* Dynamic value field */}
              {formData.calculation_method === 'percentage' ? (
                <div>
                  <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Rate (%)</label>
                  <input
                    type="number"
                    step="0.0001"
                    min="0"
                    value={formData.default_rate_percent}
                    onChange={e => setFormData({ ...formData, default_rate_percent: e.target.value })}
                    placeholder="e.g. 1.0300"
                    className={`w-full ${String(formData.default_rate_percent).trim() ? '' : 'border-rose-300 dark:border-rose-700/60 focus:border-rose-500 focus:ring-rose-500/20'}`}
                  />
                  {formErrors.default_rate_percent && <p className="text-rose-500 text-xs mt-1">{formErrors.default_rate_percent}</p>}
                </div>
              ) : (
                <div>
                  <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Fixed Amount (N$)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.default_fixed_amount}
                    onChange={e => setFormData({ ...formData, default_fixed_amount: e.target.value })}
                    placeholder="e.g. 5.00"
                    className={`w-full ${String(formData.default_fixed_amount).trim() ? '' : 'border-rose-300 dark:border-rose-700/60 focus:border-rose-500 focus:ring-rose-500/20'}`}
                  />
                  {formErrors.default_fixed_amount && <p className="text-rose-500 text-xs mt-1">{formErrors.default_fixed_amount}</p>}
                </div>
              )}

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-800 dark:text-slate-200 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={e => setFormData({ ...formData, description: e.target.value })}
                  rows={2}
                  placeholder="Optional description..."
                  className="w-full resize-none"
                />
              </div>

              {/* Active toggle */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={e => setFormData({ ...formData, is_active: e.target.checked })}
                  className="rounded border-gray-300 dark:border-slate-600 text-blue-600"
                />
                <span className="text-sm text-gray-700 dark:text-slate-300">Active</span>
              </label>

              {/* Message (inside modal) */}
              {message && showModal && (
                <p className={`text-xs font-medium px-2 py-1 rounded ${message.toLowerCase().includes('error') || message.toLowerCase().includes('cannot') || message.toLowerCase().includes('exist') ? 'text-red-600 bg-red-50 dark:bg-red-900/20 dark:text-red-400' : 'text-green-700 bg-green-50 dark:bg-green-900/20 dark:text-green-400'}`}>
                  {message}
                </p>
              )}

              {/* Footer */}
              <div className="flex justify-end gap-3 pt-2 border-t border-indigo-50 dark:border-slate-700">
                <Button type="button" onClick={closeModal} variant="secondary">Cancel</Button>
                <Button
                  type="submit"
                  variant="primary"
                  busyLabel="Saving…"
                  onClick={(e) => {
                    e.preventDefault();
                    const formEl = e.currentTarget.form;
                    if (formEl && !formEl.reportValidity()) return;
                    return handleSubmit();
                  }}
                >
                  {selected ? 'Update' : 'Add'} Expense Type
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageLoanExpenseTypes;
